import type { GrammarRules } from '~/types/grammar-rules'
import { GRAMMAR_RULE_BITS } from '~/constants/grammar'

function isBitSet(encoded: number, bit: GRAMMAR_RULE_BITS): boolean {
  return (encoded & bit) > 0
}

function isDecodedGrammarRules(value: unknown): value is GrammarRules {
  if (value === null || typeof value !== 'object') {
    return false
  }
  const o = value as Record<string, unknown>
  return (
    typeof o.rules === 'number'
    && Number.isFinite(o.rules)
    && typeof o.isMasculine === 'boolean'
    && typeof o.isFeminine === 'boolean'
    && typeof o.isIsland === 'boolean'
    && typeof o.isPluralized === 'boolean'
  )
}

/**
 * Decodes grammar rule flags from the numeric bitmask (`Attributes.GrammarRules` from the API),
 * or returns an already-decoded {@link GrammarRules} object unchanged.
 *
 * @param input The input to parse.
 * @returns The parsed grammar rules.
 *
 * @example parseGrammarRules(1) // { rules: 1, isMasculine: true, isFeminine: false, isIsland: false, isPluralized: false }
 * @example parseGrammarRules(2) // { rules: 2, isMasculine: false, isFeminine: true, isIsland: false, isPluralized: false }
 * @example parseGrammarRules(4) // { rules: 4, isMasculine: false, isFeminine: false, isIsland: true, isPluralized: false }
 * @example parseGrammarRules(8) // { rules: 8, isMasculine: false, isFeminine: false, isIsland: false, isPluralized: true }
 * @example parseGrammarRules(1 | 2 | 4 | 8) // { rules: 15, isMasculine: true, isFeminine: true, isIsland: true, isPluralized: true }
 */
export function parseGrammarRules(input: unknown = 0): GrammarRules {
  if (isDecodedGrammarRules(input)) {
    return input
  }

  const rules = typeof input === 'number' && Number.isFinite(input) ? input : 0

  return {
    rules,
    isMasculine: isBitSet(rules, GRAMMAR_RULE_BITS.IsMasculine),
    isFeminine: isBitSet(rules, GRAMMAR_RULE_BITS.IsFeminine),
    isIsland: isBitSet(rules, GRAMMAR_RULE_BITS.IsIsland),
    isPluralized: isBitSet(rules, GRAMMAR_RULE_BITS.IsPluralized),
  }
}
